import React, { useState, useContext, useEffect } from 'react';
import {
  Box, Text, Button, Textarea, VStack, Heading,
  useToast, Spinner, Badge, HStack, Divider,
  Card, CardHeader, CardBody, Flex, Alert, AlertIcon
} from "@chakra-ui/react";
import { EditIcon, CheckIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';

function EncounterNoteEditor() {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get encounter ID from URL parameters
  const params = new URLSearchParams(location.search);
  const encounterId = params.get('eid');
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [originalNote, setOriginalNote] = useState('');
  const [encounterDetails, setEncounterDetails] = useState(null);
  const [error, setError] = useState(null);
  
  // Fetch the latest note when component mounts or encounter ID changes
  useEffect(() => {
    if (!encounterId) {
      setError('No encounter ID provided');
      setLoading(false);
      return;
    }
    
    fetchLatestNote();
  }, [encounterId, accessToken]);
  
  const fetchLatestNote = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await callApi(`/v1/encounters/${encounterId}/notes/latest`, accessToken);
      const data = await response.json();
      
      if (response.ok) {
        setNoteText(data.note.note);
        setOriginalNote(data.note.note);
        setEncounterDetails({
          encounterId: data.encounter_id,
          externalEncounterId: data.external_encounter_id,
          externalCaseId: data.external_case_id,
          noteCreatedAt: data.note.note_created_at
        });
      } else {
        setError(data.error || 'Failed to fetch note');
      }
    } catch (err) {
      setError('An error occurred while fetching the note');
      console.error('Error fetching note:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (noteText.trim() === originalNote.trim()) {
      toast({
        title: 'No changes detected',
        description: 'The note has not been modified',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await callApi(`/v1/encounters/${encounterId}/notes/edit`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note_text: noteText,
          description: 'Edited via Note Editor'
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Note updated successfully',
          description: 'The note has been saved and analysis has been initiated',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Update the original note and encounter details
        setOriginalNote(noteText);
        setEncounterDetails(prev => ({
          ...prev,
          externalCaseId: data.external_case_id
        }));
      } else {
        throw new Error(data.error || 'Failed to update note');
      }
    } catch (err) {
      toast({
        title: 'Error saving note',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error('Error saving note:', err);
    } finally {
      setSaving(false);
    }
  };
  
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Calculate if there are unsaved changes
  const hasChanges = noteText.trim() !== originalNote.trim();
  
  return (
    <Box fontSize="md">
      <HeaderMenus />
      
      <Box maxW="1200px" mx="auto" p={4}>
        <HStack mb={4}>
          <Button 
            leftIcon={<ArrowBackIcon />} 
            onClick={handleBack}
            variant="outline"
          >
            Back
          </Button>
          <Heading size="lg">Encounter Note Editor</Heading>
        </HStack>
        
        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Flex justify="center" align="center" height="300px">
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            {/* Encounter Details Card */}
            {encounterDetails && (
              <Card mb={4} variant="outline">
                <CardHeader bg="blue.50" p={3}>
                  <Heading size="md">Encounter Details</Heading>
                </CardHeader>
                <CardBody>
                  <HStack spacing={4} mb={2} wrap="wrap">
                    <Badge colorScheme="blue">ID: {encounterDetails.externalEncounterId || encounterDetails.encounterId}</Badge>
                    {hasChanges && <Badge colorScheme="orange">Unsaved Changes</Badge>}
                  </HStack>
                  <Text><strong>Note Created:</strong> {formatDate(encounterDetails.noteCreatedAt)}</Text>
                  {encounterDetails.externalCaseId && (
                    <Text><strong>External Case ID:</strong> {encounterDetails.externalCaseId}</Text>
                  )}
                </CardBody>
              </Card>
            )}
            
            {/* Note Editor */}
            <Card variant="outline">
              <CardHeader bg="teal.50" p={3}>
                <Flex justify="space-between" align="center">
                  <Heading size="md">Edit Note</Heading>
                  <Button
                    rightIcon={<CheckIcon />}
                    colorScheme="teal"
                    onClick={handleSave}
                    isLoading={saving}
                    isDisabled={!hasChanges}
                  >
                    Save Changes
                  </Button>
                </Flex>
              </CardHeader>
              <CardBody>
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Note content..."
                  size="md"
                  rows={20}
                  fontFamily="monospace"
                />
                
                <Flex justify="flex-end" mt={4}>
                  <Button
                    rightIcon={<CheckIcon />}
                    colorScheme="teal"
                    onClick={handleSave}
                    isLoading={saving}
                    isDisabled={!hasChanges}
                  >
                    Save Changes
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
}

export default EncounterNoteEditor;